import React from 'react';
import { XMarkIcon } from './icons';

interface PrivacyPolicyProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-slate-900">プライバシーポリシー</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* コンテンツ */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          <div className="prose prose-slate max-w-none">
            <div className="text-slate-700 leading-relaxed space-y-6">
              <p>
                Profile Studio（以下「当サービス」）では、ユーザーのプライバシーおよび個人情報の保護を最優先に取り組んでいます。
                本ポリシーは、当サービスが提供する Web アプリ（https://profile-studio.online/）および関連サービスに適用されます。
              </p>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">1. 情報の収集</h2>
                <p>当サービスは以下の情報を収集する場合があります。</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>アカウント登録に必要な情報（氏名、メールアドレス、パスワードなど）</li>
                  <li>決済に必要な情報（クレジットカード、サブスクプラン情報など）</li>
                  <li>利用状況に関する情報（アクセスログ、クッキー等）</li>
                  <li>アップロードされた写真や生成画像データ</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">2. 情報の利用目的</h2>
                <p>収集した情報は以下の目的で利用されます。</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>サービスの提供および機能改善</li>
                  <li>決済・請求処理</li>
                  <li>ユーザーへの重要なお知らせやサポート対応</li>
                  <li>不正利用防止・セキュリティ強化</li>
                  <li>マーケティングや利用分析（同意を得た場合のみ）</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">3. 情報の共有</h2>
                <p>以下の場合を除き、第三者に情報を共有することはありません。</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>サービス提供に必要な場合（例：決済代行会社、画像処理インフラ提供会社）</li>
                  <li>法令に基づく場合</li>
                  <li>ユーザー本人の同意がある場合</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">4. データの保持</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>アカウント削除、契約終了、削除要請があれば速やかに削除します（14日以内を目安）。</li>
                  <li>法的要件により保持が必要な場合は例外とします。</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">5. ユーザーの権利</h2>
                <p>ユーザーは以下の権利を有します。</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>自身の情報へのアクセス権</li>
                  <li>情報の訂正・削除請求</li>
                  <li>利用停止・処理制限の請求</li>
                  <li>データポータビリティの請求</li>
                  <li>ダイレクトマーケティング利用に対する異議申立</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">6. オプトアウト</h2>
                <p>ニュースレターや追加サービスは、アカウント設定またはメール下部リンクから停止可能です。</p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">7. セキュリティ</h2>
                <p>当サービスはユーザー情報を保護するために以下の対策を講じます。</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>TLS による通信暗号化</li>
                  <li>セキュリティソフトの導入</li>
                  <li>外部サービス利用時の契約上の安全管理義務</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">8. ポリシー変更</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>プライバシーポリシーは必要に応じて改定されます。</li>
                  <li>重要な変更がある場合は事前に通知します。</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">9. お問い合わせ</h2>
                <p>
                  プライバシーポリシーに関するお問い合わせは、以下までご連絡ください。
                </p>
                <p className="font-medium text-blue-600">
                  <a href="mailto:support@profile-studio.online" className="hover:underline">
                    support@profile-studio.online
                  </a>
                </p>
              </section>

              <div className="mt-8 pt-6 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  最終更新日: {new Date().toLocaleDateString('ja-JP')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* フッター */}
        <div className="border-t p-4 bg-slate-50 text-center">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};
